<script>
import { layoutComputed } from "@/state/helpers";
import { inboxAlert } from "@/state/inbox-alert";
import Vertical from "./vertical";

/**
 * The wrapper every signed-in page renders inside.
 *
 * It owns the waiting-queue alert for exactly that reason: this component is
 * mounted on Users, on Roles, on the knowledge base, on the inbox itself, so
 * the poller it starts survives navigating between them. Putting it in the
 * inbox screen -- where it used to live -- meant leaving that screen killed the
 * alert, which is the one case a sound is for. See state/inbox-alert.js.
 */
export default {
  components: { Vertical },
  data() {
    return { alert: inboxAlert.state };
  },
  computed: {
    ...layoutComputed
  },
  created() {
    inboxAlert.attach();
  },
  beforeDestroy() {
    // attach/detach rather than start/stop: a route change mounts the next
    // page's layout before unmounting this one, so a plain stop() here would
    // kill the poller the first time the agent clicks a menu item. The service
    // counts mounts and only really stops on sign-out. See inbox-alert.js.
    inboxAlert.detach();
  },
  methods: {
    /** The banner is a shortcut, not a notice: an agent who hears the tone on
     *  another screen wants to be IN the conversation, not told about it. */
    goToInbox() {
      inboxAlert.dismiss();
      if (this.$route.path !== "/inbox") this.$router.push("/inbox");
    },
    dismiss() {
      inboxAlert.dismiss();
    }
  }
};
</script>

<template>
  <div>
    <vertical v-if="layoutType === 'vertical'" :layout="layoutType">
      <slot />
    </vertical>

    <!--
      Shown on whatever page the agent is on. Deliberately not a bootstrap-vue
      toast: those mount into a container that belongs to whichever component
      called them, and this one has to outlive route changes.
    -->
    <transition name="qa-fade">
      <div v-if="alert.fresh" class="queue-alert" role="alert">
        <i class="mdi mdi-account-clock queue-alert__icon"></i>
        <div class="queue-alert__body">
          <div class="queue-alert__who">
            {{ $t("inbox.waitingNotice", [alert.fresh.contact.label]) }}
          </div>
          <div class="queue-alert__why">
            {{ alert.fresh.handover_reason || $t("inbox.needsHuman") }}
            <span v-if="alert.freshExtra">
              &middot; {{ $t("inbox.andMoreWaiting", [alert.freshExtra]) }}
            </span>
          </div>
        </div>
        <button type="button" class="queue-alert__go" @click="goToInbox">
          {{ $t("inbox.openInbox") }}
        </button>
        <button type="button" class="queue-alert__x" :title="$t('label.close')" @click="dismiss">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
/* Bottom-right, above everything. Top-centre would cover the page title the
   agent is reading; bottom-left is where the sidebar lives. */
.queue-alert {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1060;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 8px;
  border-left: 4px solid #c92a2a;
  background: #fff;
  box-shadow: 0 8px 24px rgba(18, 38, 63, 0.18);
}

.queue-alert__icon {
  font-size: 22px;
  color: #c92a2a;
}

.queue-alert__body {
  min-width: 0;
}

.queue-alert__who {
  font-weight: 600;
  color: #343a40;
}

/* The reason can be long and is the least important line here, so it is the
   one that truncates. */
.queue-alert__why {
  font-size: 12px;
  color: #74788d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-alert__go {
  flex: 0 0 auto;
  border: 0;
  border-radius: 6px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: #fff;
  background: #7c4dff;
}

.queue-alert__x {
  flex: 0 0 auto;
  border: 0;
  background: none;
  color: #adb5bd;
  font-size: 16px;
  line-height: 1;
  padding: 0;
}

.qa-fade-enter-active,
.qa-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.qa-fade-enter,
.qa-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
